import { AfterViewInit, Component, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PickedAndPackedResponse } from "../../../../models/picked-and-packed/picked-and-packed-response.model";
import { PickingSaveRequest } from "../../../../models/picked-and-packed/picking-save-request.model";
import { MessageBeep } from 'src/app/wings-mobile/services/messageBeep/messageBeep.service';
import { PickingService } from 'src/app/wings-mobile/services/outbound/picking/picking.service';
import { Globals } from 'src/app/wings-mobile/shared/globals/globals';
import { InputFields } from "../../../../models/inputs/input-fields.model";
import { CharacterRemoverPipe } from '../../../../shared/charecter-remover.pipe';
import { FeatureToggleService } from 'src/app/wings-mobile/services/featureToggle/featureToggle.service';
import { forkJoin, interval, Subscription } from 'rxjs';
import { map, filter, take } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { NgClass, NgIf, NgFor } from '@angular/common';
import { ButtonDirective } from 'primeng/button';
import { AlertCustomComponent } from '../../../../shared/alert-custom/alert-custom.component';
import { DialogModule } from 'primeng/dialog';
import { InputGenericComponent } from '../../../../shared/input-generic/input-generic.component';
import { ToastModule } from 'primeng/toast';

@Component({
    selector: 'picking',
    templateUrl: './picking.component.html',
    styleUrls: ['./picking.component.scss'],
    providers: [ConfirmationService],
    imports: [FormsModule, InputTextModule, NgClass, NgIf, NgFor, ButtonDirective, AlertCustomComponent, DialogModule, InputGenericComponent, ToastModule, TranslatePipe]
})

export class PickingComponent implements OnInit, AfterViewInit, OnDestroy {
	selectedPicking: PickedAndPackedResponse;
	locationPartSelected: string = "";
	partSelected: string = "";
	conversionPartSelected: string = "";
	labelType: string = "";
	isbulk: boolean = false;
	multiple: string = "";
	pickingSaveRequest: PickingSaveRequest;
	characterRemover: CharacterRemoverPipe = new CharacterRemoverPipe;

	inputFields: InputFields[];
	serialNumbers: string[] = [''];
	serialNumberErrors: boolean[] = [false];
	serialNumberValidated: boolean[] = [false];
	shouldShowSerialNumberToEdit: boolean = false;
	shouldShowSerialNumberModal: boolean = false;

	featureToggleSerialNumber: boolean = false;
	isAutomaticPickingHandHeldEnabled: boolean = false;

	errorTitle: string;
	errorMessage: string;
	errorFocus: string;
	showAlert: boolean;

	lastPickedLabel: string = "";
	lastPickedLocation: string = "";
	lastPickedPart: string = "";
	lastPickedQuantity: number = null;
	showLastPicking: boolean = false;

	private globalsSubscription: Subscription;

	constructor(
		private beep: MessageBeep,
		private translate: TranslateService,
		private renderer: Renderer2,
		private pickingService: PickingService,
		private globals: Globals,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private featureToggleService: FeatureToggleService,
	) {
		this.beep = beep;
		this.translate = translate;
		this.renderer = renderer;
		this.pickingService = pickingService;
		this.globals = globals;
		this.messageService = messageService;
		this.confirmationService = confirmationService;
		this.inputFields = [
			{ name: 'pick_label', id: 'pick_label', focus: true, value: null, action: 'this.findByLabel()', valid: false },
			{ name: 'pick_location', id: 'pick_location', focus: false, value: null, action: 'this.checkLocation()', valid: false },
			{ name: 'pick_part', id: 'pick_part', focus: false, value: null, action: 'this.checkPart()', valid: false },
			{ name: 'pick_quantity', id: 'pick_quantity', focus: false, value: null, action: 'this.checkQuantity()', valid: false },
		];
	}

	ngOnInit(): void {
		this.initializeComponentState();
		this.waitForGlobalsAndCheckFeatureToggles();
	}

	ngAfterViewInit(): void {
        this.renderer.selectRootElement(`#${this.inputFields[0].id}`).focus();
    }

	ngOnDestroy(): void {
		if (this.globalsSubscription) {
			this.globalsSubscription.unsubscribe();
		}
	}

	private waitForGlobalsAndCheckFeatureToggles(): void {
		const initialSubdivision = this.globals.user?.warehouse?.bkSubDivisionUidD;
		if (initialSubdivision) {
			this.checkAllFeatureToggles();
			return;
		}

		this.globalsSubscription = interval(100)
			.pipe(
				map(() => this.globals.user?.warehouse?.bkSubDivisionUidD),
				filter(subdivision => !!subdivision),
				take(1)
			)
			.subscribe(subdivision => {
				this.checkAllFeatureToggles();
			});
	}

	initializeComponentState() {
		this.clearCurrentPickingForm();
		this.lastPickedLabel = "";
		this.lastPickedLocation = "";
		this.lastPickedPart = "";
		this.lastPickedQuantity = null;
		this.showLastPicking = false;
	}

	resetPickingRelatedStateExceptLabel() {
		this.inputFields[1].value = null;
		this.inputFields[1].valid = false;
		this.inputFields[2].value = null;
		this.inputFields[2].valid = false;
		this.inputFields[3].value = null;
		this.inputFields[3].valid = false;

		this.selectedPicking = null;
		this.locationPartSelected = "";
		this.conversionPartSelected = "";
		this.partSelected = "";
		this.labelType = "";
		this.isbulk = false;
		this.multiple = "";

		this.serialNumbers = [''];
		this.serialNumberErrors = [false];
		this.serialNumberValidated = [false];
		this.shouldShowSerialNumberToEdit = false;
		this.shouldShowSerialNumberModal = false;
	}

	findByLabel() {
		const currentLabelValue = this.inputFields[0].value;

		this.resetPickingRelatedStateExceptLabel();

		if (!currentLabelValue || String(currentLabelValue).trim() === '') {
			this.showErrorModal('modal.error', 'modal.labelNotFound', 'pick_label');
			this.inputFields[0].value = null;
			this.inputFields[0].valid = false;
			return;
		}

		this.pickingService.getPicking(currentLabelValue, this.globals.user.warehouse?.bkSubDivisionUidD).subscribe(
			res => {
				setTimeout(() => {
					if (res == null || res.length === 0) {
						this.inputFields[0].value = null;
						this.inputFields[0].valid = false;
						this.showErrorModal('modal.error', 'modal.labelNotFound', 'pick_label');
					} else {
						let foundValidPicking = false;
						res.forEach(pick => {
							if (pick.status.bkStatusR == 20) {
								this.selectedPicking = pick;
								this.inputFields[0].valid = true;
								this.locationPartSelected = this.selectedPicking.whXLocation.bkLocationC;
								this.country();
								this.labeltypeFunc();
								this.focusInputFields('pick_location');
								foundValidPicking = true;
							}
						});
						if (!foundValidPicking) {
							this.inputFields[0].value = null;
							this.inputFields[0].valid = false;
							this.showErrorModal('modal.error', 'modal.labelIsNotPrinted', 'pick_label');
						}
					}
				}, 0);
			},
			error => {
				setTimeout(() => {
					this.inputFields[0].value = null;
					this.inputFields[0].valid = false;
					this.showErrorModal('modal.error', 'modal.pickedError', 'pick_label');
				}, 0);
			}
		);
	}

	checkAllFeatureToggles() {
		const subdivision = this.globals.user.warehouse?.bkSubDivisionUidD;
		if (subdivision) {

			forkJoin([
				this.featureToggleService.featureSerialNumberEnable(subdivision),
			]).subscribe({
				next: ([serialNumberResponse]) => {
					this.featureToggleSerialNumber = serialNumberResponse;
				},
				error: (error) => {
					this.featureToggleSerialNumber = false;
				}
			});

			forkJoin([
				this.featureToggleService.checkAutomaticPickingHandHeldToggle(subdivision)
			]).subscribe({
				next: ([automaticPickingResponse]) => {
					this.isAutomaticPickingHandHeldEnabled = automaticPickingResponse;
					this.showLastPicking = this.isAutomaticPickingHandHeldEnabled;
				},
				error: (error) => {
					this.isAutomaticPickingHandHeldEnabled = false;
					this.showLastPicking = false;
				}
			});

		} else {
			this.featureToggleSerialNumber = false;
			this.isAutomaticPickingHandHeldEnabled = false;
			this.showLastPicking = false;
		}
	}

	checkLocation() {
		this.inputFields[1].value = this.characterRemover.transform(this.inputFields[1].value, " *").toUpperCase();
		if (this.inputFields[1].value == this.locationPartSelected || this.inputFields[1].value == "99999") {
			this.inputFields[1].valid = true;
			this.focusInputFields("pick_part");
		} else {
			this.showErrorModal('modal.error', 'modal.locationNotFound', 'pick_location');
		}
	}

	checkPart() {
		this.inputFields[2].value = this.characterRemover.transform(this.inputFields[2].value, " *").toUpperCase();
		if (this.isPartValid(this.inputFields[2].value)
			// Fallback: retry stripping "-" from the whole input, regardless of a leading "P"
			|| this.isPartValid(this.inputFields[2].value.replace(/-/g, ""))
		) {
			this.inputFields[2].valid = true;
			if (this.selectedPicking.partsXSubDivision.needSerial && this.featureToggleSerialNumber && this.validMarket()) {
				const partQ = this.selectedPicking.partQ;
				this.serialNumbers = Array(partQ).fill('');
				this.serialNumberErrors = Array(partQ).fill(false);
				this.serialNumberValidated = Array(partQ).fill(false);
				this.shouldShowSerialNumberModal = true;
			} else {
				if (this.isAutomaticPickingHandHeldEnabled && this.inputFields[3].valid && this.inputFields[3].value !== null) {
					this.triggerAutomaticSave();
				} else {
					this.focusInputFields('pick_quantity');
				}
			}
		} else {
			this.showErrorModal('modal.error', 'modal.partNotFound', 'pick_part');
		}
	}

	isPartValid(value: string): boolean {
		return value == this.partSelected
			|| value == "99999"
			|| this.validateOtherPartsStandard(value)
			|| (this.conversionPartSelected && value == this.conversionPartSelected)
			|| this.validatePartCodes(value);
	}

	validatePartCodes(value: string = this.inputFields[2].value): boolean {
		const partCodes = this.selectedPicking?.partCodes;
		if (!partCodes || partCodes.length === 0) {
			return false;
		}
		const inputNormalized = value.split(/\s/).join('').toUpperCase();
		return partCodes.some(code => {
			if (code.engVtprC) {
				const engNormalized = code.engVtprC.split(/\s/).join('').toUpperCase();
				if (engNormalized === inputNormalized) {
					return true;
				}
			}
			if (code.finisVtprC) {
				if (code.finisVtprC.toUpperCase() === inputNormalized) {
					return true;
				}
			}
			return false;
		});
	}

	validateOtherPartsStandard(value: string = this.inputFields[2].value): boolean {
		let valid = false;
		let partsField = new String(value);

		if(partsField.startsWith("P",0)){
			partsField = partsField.substring(1);
			partsField = partsField.replace(/-/g, "");
		}

		const partsTO = this.selectedPicking.partsXSubDivision.partsTO;
		const excludedKeys = new Set(['conversionPart', 'pagination']);

		Object.keys(partsTO).forEach(part => {
			if(!excludedKeys.has(part) && partsTO[part] == partsField){
				valid = true;
			}
		});

		const conversionPart = partsTO.conversionPart;
		if (conversionPart) {
			Object.keys(conversionPart).forEach(part => {
				if(conversionPart[part] == partsField){
					valid = true;
				}
			});
		}

		return valid;
	}

	validMarket() {
		return	this.selectedPicking.workOrderItems.transactionTypeF == 'E' ||
				this.selectedPicking.workOrderItems.transactionTypeF == 'W' ||
				this.selectedPicking.workOrderItems.transactionTypeF == null ||
				this.selectedPicking.workOrderItems.transactionTypeF == undefined ||
				!this.selectedPicking.workOrderItems.transactionTypeF
	}

	private handleInvalidQuantity(alertMessageKey: string): void {
		this.inputFields[3].value = null;
		this.inputFields[3].valid = false;
		this.beep.showAlertCustom('modal.error', alertMessageKey, 'pick_quantity');
	}

	checkQuantity() {
		if(this.inputFields[3].value !== null) {
			if (this.inputFields[3].value == 0) {
				this.inputFields[3].valid = true;
			} else if (this.inputFields[3].value == this.selectedPicking.partQ) {
				this.inputFields[3].valid = true;
			} else if (this.inputFields[3].value > 0 && this.inputFields[3].value <= this.selectedPicking.partQ) {
				if (this.inputFields[3].value % this.selectedPicking.partsXSubDivision.unityOfIssues[0].unityOfIssueA == 0) {
					this.inputFields[3].valid = true;
				} else {
					this.inputFields[3].value = null;
					this.beep.showAlertCustom('modal.error', 'modal.quantityIsNotMultiple', 'quantity');
				}
			} else if (this.inputFields[3].value > this.selectedPicking.partQ) {
				this.inputFields[3].value = null;
				this.beep.showAlertCustom('modal.error', 'modal.quantityGreaterThanLocation', 'quantity');
			} else {
				this.inputFields[3].value = null;
				this.beep.showAlertCustom('modal.error', 'modal.quantityLessOrEqualToZero', 'quantity');
			}
		}
	}

	triggerAutomaticSave() {
		if (!this.inputFields[0].valid || !this.inputFields[1].valid || !this.inputFields[2].valid || !this.inputFields[3].valid) {
			const firstInvalidField = this.inputFields.find(field => !field.valid);
			if (firstInvalidField) {
				this.showErrorModal('modal.error', 'modal.fillAllFields', firstInvalidField.id);
			}
			return;
		}
		this.confirmAndSavePicking();
	}

	checkPickingSave() {
		if (this.checkInputs()) {
			const firstInvalidField = this.inputFields.find(field => !field.valid);
			if (firstInvalidField) {
				this.showErrorModal('modal.error', 'modal.fillAllFields', firstInvalidField.id);
			}
			return;
		}
		this.confirmAndSavePicking();
	}

	confirmAndSavePicking() {
		if (!this.checkInputs()) {
			if (this.inputFields[3].value == 0) {
				if (confirm(this.translate.instant("modal.DenialLabel"))) {
					this.savePicking();
				}
			} else if (this.inputFields[3].value < this.selectedPicking.partQ) {
				if (confirm(this.translate.instant("modal.PickingAndDenialLabel"))) {
					this.savePicking();
				}
			} else {
				this.savePicking();
			}
		}
	}

	savePicking() {
		this.pickingService.savePicking(this.setSaveRequest()).subscribe({
			next: () => {
				setTimeout(() => {
					this.messageService.add({
						key: 'tc',
						severity: 'success',
						summary: this.translate.instant('message.success'),
						detail: this.translate.instant('modal.picked')
					});

					if (this.isAutomaticPickingHandHeldEnabled) {
						this.lastPickedLabel = this.inputFields[0].value;
						this.lastPickedLocation = this.inputFields[1].value;
						this.lastPickedPart = this.inputFields[2].value;
						this.lastPickedQuantity = this.inputFields[3].value;
					} else {
						this.lastPickedLabel = "";
						this.lastPickedLocation = "";
						this.lastPickedPart = "";
						this.lastPickedQuantity = null;
					}

					this.clearCurrentPickingForm();
				}, 0);
			}, error: () => {
				setTimeout(() => {
					this.clearCurrentPickingForm();
					this.showErrorModal('modal.error', 'modal.pickedError', 'pick_label');
				}, 0);
			}
		});
	}

	setSaveRequest(): PickingSaveRequest {
		this.pickingSaveRequest = {
			workOrderItemDetailUidK: this.selectedPicking.workOrderItemDetailUidK,
			bkWorkOrderItemUidD: this.selectedPicking.workOrderItems.workOrderItemUidK,
			statusUidD: this.selectedPicking.statusUidD,
			warehouseUidD: this.selectedPicking.warehouseUidD,
			partQ: this.inputFields[3].value,
			outboundBatchControlUidD: this.selectedPicking.outboundBatchControl.outboundBatchControlUidK,
			outboundCaseUidD: this.selectedPicking.outboundCaseUidD,
			pickingLabelR: this.selectedPicking.pickingLabelR + "",
			outboundCases: {
				subDivisionUidD: this.globals.user.warehouse.bkSubDivisionUidD + "",
			},
			whXLocation: {
				bkLocationC: this.selectedPicking.whXLocation.bkLocationC,
				locationType: {
					bulkTypeF: this.selectedPicking.whXLocation.locationType.bulkTypeF
				}
			},
			workOrderItemDetailsSerial: this.serialNumbers.filter((serie) => {
				return serie
			})
				.map((serie) => {
					return { "bkSerialC": serie, "workOrderItemDetailUidD": this.selectedPicking.workOrderItemDetailUidK };
				}),
			defaultPartCode: this.inputFields[2].value === '99999',
			defaultLocationCode: this.inputFields[1].value === '99999',
		}
		return this.pickingSaveRequest;
	}

	labeltypeFunc() {
		if (this.selectedPicking.whXLocation.locationType.bulkTypeF == 'N') {
			this.labelType = "- BIN";
			this.isbulk = false;
			this.inputFields[3].value = this.selectedPicking.partQ;
			this.inputFields[3].valid = true;
			this.multiple = "" + this.selectedPicking.partsXSubDivision.unityOfIssues[0].unityOfIssueA;
		} else {
			this.labelType = "- BULK";
			this.inputFields[3].value = this.selectedPicking.partQ;
			this.inputFields[3].valid = true;
			this.isbulk = true;
			this.multiple = "";
		}
	}

	clearCurrentPickingForm() {
		this.inputFields.forEach(i => {
			i.valid = false;
			i.value = null;
		});
		this.resetVariables();
		this.serialNumbers = [''];
		this.serialNumberErrors = [false];
		this.serialNumberValidated = [false];
		this.shouldShowSerialNumberToEdit = false;
		this.shouldShowSerialNumberModal = false;
		this.focusInputFields("pick_label");
	}

	resetVariables() {
		this.selectedPicking = null;
		this.locationPartSelected = "";
		this.partSelected = "";
		this.conversionPartSelected = "";
		this.labelType = "";
	}

	country() {
		const partsTO = this.selectedPicking.partsXSubDivision.partsTO;
		const conversionPart = partsTO.conversionPart;
		switch (this.globals.user.country) {
			case "CHL":
				this.partSelected = partsTO.popimsChileC;
				this.conversionPartSelected = conversionPart?.popimsChileC || "";
				break;
			case "PER":
				this.partSelected = partsTO.popimsPeruC;
				this.conversionPartSelected = conversionPart?.popimsPeruC || "";
				break;
			case "BRA":
			case "ARG":
			default:
				this.partSelected = partsTO.popimsArgBraC;
				this.conversionPartSelected = conversionPart?.popimsArgBraC || "";
				break;
		}
	}

	showErrorModal(title: string, msg: string, focus?: string) {
		this.errorTitle = this.translate.instant(title);
		this.errorMessage = this.translate.instant(msg);
		this.errorFocus = focus;
		this.showAlert = true;
		this.beep.errorBeep();
	}

	hideErrorModal(elId: string) {
		if (elId) {
			this.focusInputFieldsOnError(elId);
		}
		this.inputFields.forEach(item => {
			if (item.id === elId) {
				item.value = null;
				item.valid = false;
			}
		});
		this.showAlert = false;
	}

	executeAction(item) {
		this.inputFields.forEach(input => {
			if (input.id == item.id) {
				input.value = item.value;
			}
		});

		switch (item.action) {
			case 'this.findByLabel()':
				this.findByLabel();
				break;
			case 'this.checkLocation()':
				this.checkLocation();
				break;
			case 'this.checkPart()':
				this.checkPart();
				break;
			case 'this.checkQuantity()':
				this.checkQuantity();
				break;
			default:
				break;
		}
	}

	focusInputFields(id: string) {
		this.blurInputFields();
		this.renderer.selectRootElement('#scanner_id').focus();
		this.focusOn(id);
	}

	focusInputFieldsOnError(id: string) {
		this.blurInputFields();
		this.focusOn(id);
		setTimeout(() => {
            const element = this.renderer.selectRootElement(`#${id}`);
            if (element) {
                element.focus();
            }
        }, 0);
	}

	blurInputFields() {
		this.inputFields.forEach(input => input.focus = false);
	}

	focusOn(elId: string) {
		switch (elId) {
			case "pick_label":
				this.inputFields[0].focus = true;
				this.inputFields[1].focus = false;
				this.inputFields[2].focus = false;
				this.inputFields[3].focus = false;
				break;
			case "pick_location":
				this.inputFields[0].focus = false;
				this.inputFields[1].focus = true;
				this.inputFields[2].focus = false;
				this.inputFields[3].focus = false;
				break;
			case "pick_part":
				this.inputFields[0].focus = false;
				this.inputFields[1].focus = false;
				this.inputFields[2].focus = true;
				this.inputFields[3].focus = false;
				break;
			case "pick_quantity":
				this.inputFields[0].focus = false;
				this.inputFields[1].focus = false;
				this.inputFields[2].focus = false;
				this.inputFields[3].focus = true;
				break;
		}
	}

	checkValidCharacter(event: KeyboardEvent) {
		if (this.isbulk) {
			if (this.isbulk && ['Backspace', 'Enter'].includes(event.key)) {
				return true;
			}
			if (['0', '1'].includes(event.key)) {
				return true;
			}
			return false;
		}
		return true;
	}

	onInsertUnit(event) {
		if (event.key === '.' || event.key === '-' || event.key === '+' || event.key === 'e') {
			event.preventDefault();
		}
	}

	trackByIndex(index: number, item: any): number {
		return index;
	}

	checkAllSerialNumbers() {
		if (this.areAllSerialNumbersFilled()) {
			this.shouldShowSerialNumberToEdit = true;
			this.shouldShowSerialNumberModal = false;
			this.messageService.add({
				key: 'tc',
				severity: 'success',
				summary: this.translate.instant('message.success'),
				detail: this.translate.instant('modal.serialNumber')
			});

			this.renderer.selectRootElement('#pick_quantity').focus();
		}
	}

	areAllSerialNumbersFilled(): boolean {
		return this.serialNumbers.every(serialNumber => serialNumber.trim() !== '');
	}

	hasSerialNumberErrors(): boolean {
		return this.serialNumberErrors.some(error => error === true);
	}

	areAllSerialNumbersValidated(): boolean {
		return this.serialNumberValidated.every(validated => validated === true);
	}

	checkSerialNumber(index: number) {
		const serialNumber = this.serialNumbers[index];
		this.serialNumberErrors[index] = false;
		this.serialNumberValidated[index] = false;

		if (this.serialNumbers.filter(sn => sn === serialNumber).length > 1) {
			this.serialNumberErrors[index] = true;
			this.serialNumberValidated[index] = true;
			return;
		}

		if (serialNumber) {
			this.pickingService.checkSerialNumber(serialNumber).subscribe({
				next: (response) => {
					setTimeout(() => {
						if (response.used) {
							this.serialNumberErrors[index] = true;
						}
						this.serialNumberValidated[index] = true;
					}, 0);
				},
				error: () => {
					setTimeout(() => {
						this.shouldShowSerialNumberModal = false;
						this.clearCurrentPickingForm();
						this.showErrorModal('modal.error', 'modal.pickedError', 'pick_label');
						this.serialNumberValidated[index] = true;
					}, 0);
				}
			});
		}
	}

	allowOnlyAlphanumeric(event: KeyboardEvent) {
		const charCode = event.charCode;
		if((charCode >= 48 && charCode <= 57) ||
			(charCode >= 65 && charCode <= 90) ||
			(charCode >= 97 && charCode <= 122))
    {
			return true;
		} else {
			event.preventDefault();
			return false;
		}
	}

	checkInputs(): boolean {
		return this.inputFields.some(item => !item.valid);
	}
}

